import { ReactNode, useEffect, useState, useRef } from 'react';
import { IoMdClose } from 'react-icons/io';

interface PopupWrapperProps {
  title: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  size?: 'sm' | 'md' | 'mlg' | 'lg' | 'xl' | 'full';
  className?: string;
  logo?: ReactNode;
  headerComponent?: ReactNode;
  footer?: ReactNode;
}

const PopupWrapper: React.FC<PopupWrapperProps> = ({
  title,
  isOpen,
  onClose,
  children,
  size = 'md',
  className = '',
  logo,
  headerComponent,
  footer,
}) => {
  const [animationState, setAnimationState] = useState<'closed' | 'opening' | 'open' | 'closing'>(
    'closed'
  );

  const wasOpen = useRef(isOpen);

  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      setAnimationState('closed');
      document.body.style.overflow = 'auto';
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen && animationState === 'closed') {
      setAnimationState('opening');
      setTimeout(() => setAnimationState('open'), 10);
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      if (animationState === 'closing' || animationState === 'closed') {
        document.body.style.overflow = 'auto';
      }
    };
  }, [isOpen, animationState]);

  const handleClose = () => {
    setAnimationState('closing');
    document.body.style.overflow = 'auto';
    setTimeout(() => {
      setAnimationState('closed');
      onClose();
    }, 300);
  };

  if (!isOpen && animationState === 'closed') return null;

  const sizeClasses = {
    sm:   'w-[95%] sm:w-[400px]',
    md:   'w-[95%] sm:w-[550px]',
    mlg:  'w-[95%] sm:w-[70%] max-w-[750px]',
    lg:   'w-[95%] sm:w-[70%] max-w-[900px]',
    xl:   'w-[95%] sm:w-[70%] max-w-[1200px]',
    full: 'w-[95%] sm:w-[70%] max-w-[90%]',
  };

  const animationClasses = {
    opening: 'translate-y-[80vh]',
    open:    'translate-y-0',
    closing: 'translate-y-[80vh]',
    closed:  'translate-y-[80vh]',
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out ${
          animationState === 'open' ? 'bg-opacity-50' : 'bg-opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Popup shell — flex-col so footer pins to bottom */}
      <div
        className={`bg-white rounded-2xl shadow-xl transform transition-all duration-300 ease-in-out
          flex flex-col max-h-[90vh]
          ${sizeClasses[size]} mx-auto my-auto relative z-10 ${className} ${animationClasses[animationState]}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header — never scrolls */}
        <div className="flex items-center justify-between px-10 pt-10 flex-shrink-0">
          <div className="flex items-center py-4 flex-grow">
            {logo && <div className="mr-3">{logo}</div>}
            <h2 className="text-4xl font-semibold text-gray-800">{title}</h2>
            {headerComponent && <div className="mr-auto ml-4">{headerComponent}</div>}
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close popup"
          >
            <IoMdClose size={38} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-10 pb-6">{children}</div>

        {/* Fixed footer — only rendered when footer prop is passed */}
        {footer && (
          <div className="flex-shrink-0 px-10 pb-8 pt-4 border-t border-gray-100">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default PopupWrapper;
